import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  BarChart3,
  Search,
  Activity,
  FileText,
  FlaskConical,
  Target,
  Layers,
  Network,
  Link2,
  Calendar,
  Unlink,
  AlertTriangle,
  Share2,
  MessageCircle,
  MapPin,
  Users,
  Megaphone,
  Globe,
  TrendingUp,
  Upload,
  Sparkles,
  Code,
  Image,
  ImagePlus,
  MousePointerClick,
  ClipboardCheck,
  Gauge,
  Bot,
  Brain,
  Radar,
  ScrollText,
  PenTool,
  RefreshCw,
  Shield,
  HeartPulse,
  Wrench,
  Palette,
  Clock,
  Settings as SettingsIcon,
  BookOpen,
  LogOut,
  Lock,
  Radio,
  Zap,
} from "lucide-react";
import UpgradeModal from "./UpgradeModal";
import { isAgencyAdmin as checkAgencyAdmin } from "../utils/subdomain";
import api from "../api/client";

const FEATURE_MAP = {
  "/ai-insights": "ai_insights",
  "/geo": "geo_monitor",
  "/backlinks": "backlinks",
  "/alt-text": "alt_text_ai",
  "/image-library": "image_library",
  "/calendar": "calendar",
  "/reports": "pdf_reports",
};

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/keywords", icon: Search, label: "Keywords" },
  { to: "/search", icon: Activity, label: "Search" },
  { to: "/behavior", icon: MousePointerClick, label: "Behavior" },
  { to: "/content", icon: FileText, label: "Content" },
  { to: "/paa", icon: FlaskConical, label: "PAA Pipeline" },
  { to: "/competitors", icon: Target, label: "Competitors" },
  { to: "/pages", icon: Layers, label: "Landing Pages" },
  { to: "/topic-clusters", icon: Network, label: "Topic Clusters" },
  { to: "/internal-links", icon: Link2, label: "Internal Links" },
  { to: "/calendar", icon: Calendar, label: "Calendar" },
  { to: "/backlinks", icon: Unlink, label: "Backlinks" },
  { to: "/404-manager", icon: AlertTriangle, label: "404 Manager" },
  { to: "/syndication", icon: Share2, label: "Syndication" },
  { to: "/social", icon: MessageCircle, label: "Social" },
  { to: "/social-monitor", icon: Radio, label: "Social Monitor" },
  { to: "/google-business", icon: MapPin, label: "Google Business" },
  { to: "/leads", icon: Users, label: "Leads" },
  { to: "/campaigns", icon: Megaphone, label: "Campaigns" },
  { to: "/local-rankings", icon: Globe, label: "Local Rankings" },
  { to: "/rank-tracker", icon: TrendingUp, label: "Rank Tracker" },
  { to: "/bulk-import", icon: Upload, label: "Bulk Import" },
  { to: "/seo-optimizer", icon: Sparkles, label: "SEO Optimizer" },
  { to: "/schema", icon: Code, label: "Schema Markup" },
  { to: "/alt-text", icon: Image, label: "Alt Text AI" },
  { to: "/image-library", icon: ImagePlus, label: "Image Library" },
  { to: "/audit", icon: ClipboardCheck, label: "Site Audit" },
  { to: "/vitals", icon: Gauge, label: "Web Vitals" },
  { to: "/site-health", icon: HeartPulse, label: "Site Health" },
  { to: "/ai-agent", icon: Bot, label: "AI Agent" },
  { to: "/ai-insights", icon: Brain, label: "AI Insights" },
  { to: "/geo", icon: Radar, label: "GEO Monitor" },
  { to: "/reports", icon: ScrollText, label: "Reports" },
  { to: "/activity-log", icon: Clock, label: "Activity Log" },
  { to: "/page-designer", icon: PenTool, label: "Page Designer" },
  { to: "/wordpress-sync", icon: RefreshCw, label: "WordPress Sync" },
  { to: "/seo-audit", icon: Shield, label: "SEO Audit" },
  { to: "/seo-maintenance", icon: Wrench, label: "SEO Maintenance" },
  { to: "/white-label", icon: Palette, label: "White Label" },
  { to: "/site-settings", icon: SettingsIcon, label: "Site Settings" },
  { to: "/docs", icon: BookOpen, label: "Documentation" },
  { to: "/settings", icon: SettingsIcon, label: "Settings" },
];

export default function Sidebar({ onLogout, userInfo }) {
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  const [beatDown, setBeatDown] = useState(false);
  const isAgencyAdmin = checkAgencyAdmin();

  useEffect(() => {
    if (!isAgencyAdmin) return undefined;
    let active = true;
    const check = () => {
      api
        .get("/automation/status/")
        .then((res) => {
          if (active) setBeatDown(res.data.beat_alive === false);
        })
        .catch(() => {});
    };
    check();
    const id = setInterval(check, 60000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [isAgencyAdmin]);

  const features = userInfo?.features || {};
  const usage = userInfo?.usage || {};
  const plan = userInfo?.plan || "agency";
  const isSuperuser = userInfo?.user?.is_superuser;

  return (
    <aside className="w-60 bg-dark-300 border-r border-dark-50 flex flex-col min-h-screen">
      {/* Logo */}
      <div className="px-5 py-6 border-b border-dark-50">
        <h1 className="text-xl font-bold font-mono tracking-tight">
          <span className="text-copper">Auto</span>
          <span className="text-white">SEO</span>
        </h1>
        {userInfo?.organization && (
          <div className="text-[10px] text-gray-600 mt-1 truncate">{userInfo.organization.name}</div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label }) => {
          const featureKey = FEATURE_MAP[to];
          const isLocked = featureKey && features[featureKey] === false;

          if (isLocked) {
            return (
              <button
                key={to}
                onClick={() => setUpgradeFeature(featureKey)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-400 w-full text-left group transition-colors"
              >
                <Icon size={18} />
                <span className="flex-1">{label}</span>
                <Lock size={12} className="text-gray-700 group-hover:text-gray-500" />
              </button>
            );
          }

          return (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-copper/10 text-copper"
                    : "text-gray-400 hover:text-white hover:bg-dark-50/50"
                }`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          );
        })}

        {/* Automation — agency admins only */}
        {isAgencyAdmin && (
          <NavLink
            to="/automation"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-copper/10 text-copper"
                  : "text-gray-400 hover:text-white hover:bg-dark-50/50"
              }`
            }
          >
            <Zap size={18} />
            <span className="flex-1">Automation</span>
            {beatDown && (
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" title="Scheduler down" />
            )}
          </NavLink>
        )}

        {/* Admin link — superusers only */}
        {isSuperuser && (
          <NavLink
            to="/admin-dashboard"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mt-2 ${
                isActive
                  ? "bg-copper/10 text-copper"
                  : "text-gray-400 hover:text-white hover:bg-dark-50/50"
              }`
            }
          >
            <Shield size={18} />
            Admin
          </NavLink>
        )}
      </nav>

      {/* Usage bars */}
      {usage.pages_per_month && (
        <div className="px-4 py-3 border-t border-dark-50 space-y-2">
          {[
            { key: "pages_per_month", label: "Pages" },
            { key: "keywords", label: "Keywords" },
          ].map(({ key, label }) => {
            const u = usage[key];
            if (!u) return null;
            const pct = u.limit > 0 && u.limit < 999 ? Math.min((u.used / u.limit) * 100, 100) : 0;
            const color = pct > 90 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-copper";
            return (
              <div key={key}>
                <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
                  <span>{label}</span>
                  <span>{u.used}/{u.limit >= 999 ? "\u221e" : u.limit}</span>
                </div>
                <div className="w-full bg-dark-50 rounded-full h-1">
                  <div className={`${color} rounded-full h-1 transition-all`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="px-3 py-4 border-t border-dark-50 space-y-3">
        <button
          onClick={onLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-dark-50/50 transition-colors w-full"
        >
          <LogOut size={18} />
          Logout
        </button>
        <div className="px-2 text-xs text-gray-500 font-mono">
          v6.0.0 \u2014 Phase 6
        </div>
      </div>

      {upgradeFeature && (
        <UpgradeModal
          onClose={() => setUpgradeFeature(null)}
          currentPlan={plan}
          requiredFeature={upgradeFeature}
        />
      )}
    </aside>
  );
}
